/**
 * The single writer of `document.title`.
 *
 * Two things want to own the tab title and neither can win on its own: the route
 * (Header, HeaderClient and useSetPageTitle each set a page name, and
 * useSetPageTitle resets it on unmount) and the unread badge (which must survive
 * every one of those route changes). When both assigned directly, whichever ran
 * last took the tab — so navigating anywhere silently dropped the unread count.
 *
 * Composing them here means neither caller has to know the other exists: routes
 * set a base, messaging sets a count, and the rendered title is always both.
 */

const DEFAULT_DOCUMENT_TITLE = 'Dwight Funding - Atlas'

/** Past this the exact number stops being information and starts being noise. */
const MAX_DISPLAYED_COUNT = 99

let baseTitle = DEFAULT_DOCUMENT_TITLE
let unreadCount = 0

/**
 * Shared with the favicon badge, which renders the same number a few pixels away —
 * two independent caps would eventually disagree and show `99+` on one and a raw
 * count on the other.
 */
export const formatUnreadCount = (count: number): string =>
  count > MAX_DISPLAYED_COUNT ? `${MAX_DISPLAYED_COUNT}+` : String(count)

const render = (): void => {
  document.title = unreadCount > 0 ? `(${formatUnreadCount(unreadCount)}) ${baseTitle}` : baseTitle
}

/** The page's own name. Empty falls back to the app name rather than a blank tab. */
export const setBaseTitle = (title: string): void => {
  baseTitle = title || DEFAULT_DOCUMENT_TITLE
  render()
}

export const resetBaseTitle = (): void => setBaseTitle(DEFAULT_DOCUMENT_TITLE)

/** Unread messages, prefixed onto whatever the current page is called. */
export const setUnreadTitleCount = (count: number): void => {
  const next = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  if (next === unreadCount) return
  unreadCount = next
  render()
}
